import './ImageEditor.less';
import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { CSSTransitionGroup } from 'react-transition-group';
import { transition, exportCanvasImage } from 'lib/helpers';
import { RectTool, EllipseTool, ArrowTool, PathTool, TextTool, MosaicTool } from './tools';
import { Toolbar } from './Toolbar';
import { BrushToolbar, BRUSH_COLOR_MAP, BRUSH_SIZE_MAP, TEXT_SIZE_MAP } from './BrushToolbar';

const TOOLS = {
	rect: RectTool,
	ellipse: EllipseTool,
	arrow: ArrowTool,
	path: PathTool,
	text: TextTool,
	mosaic: MosaicTool,
};

/** @type {ImageEditor} */
let instance = null;

/** @type {HTMLDivElement} */
let element = null;

export class ImageEditor extends React.Component {
	static edit({ imageUrl, imageSizeLimit, uploadImage, onFinish, onCancel }) {
		if (!element) {
			element = document.createElement('div');
			document.body.appendChild(element);
			ReactDOM.render(<ImageEditor />, element);
		}
		if (instance) {
			instance.start({ imageUrl, imageSizeLimit, uploadImage, onFinish, onCancel });
		}
	}

	constructor(...args) {
		super(...args);
		// singletone
		instance = this;
		this.state = this.getInitialState();
	}

	getInitialState() {
		return {
			imageUrl: null,
			imageLoading: true,
			commiting: false,
			width: 0,
			height: 0,
			mode: null,
			drawings: [],
			activeDrawing: null,
			brushWidth: BRUSH_SIZE_MAP.normal,
			brushColor: BRUSH_COLOR_MAP.red,
			fontSize: TEXT_SIZE_MAP.normal,
			fontFamily: 'Microsoft Yahei',
			toolState: {},
		}
	}

	/** @type {HTMLImageElement} */
	image = null;

	/** @type {HTMLCanvasElement} */
	canvas = null;

	tools = this.createTools();

	render() {
		const {
			imageUrl,
			imageLoading,
			commiting,
			commitError,
			onFinish,
			onCancel,
			width,
			height,
			mode,
			drawings,
			brushWidth,
			brushColor,
			fontFamily,
			fontSize,
			grabbing,
		} = this.state;

		const cursor = mode ? this.tools[mode].cursor : grabbing ? '-webkit-grabbing' : '-webkit-grab';
		const toolList = [
			this.tools.rect,
			this.tools.ellipse,
			this.tools.arrow,
			this.tools.path,
			this.tools.text,
			this.tools.mosaic,
		];
		return (
			<CSSTransitionGroup component="div" {...transition.fade()} className="draft-image-editor">
				{ imageUrl &&
					<div className="draft-image-edit-zone" ref={dom => this.rootElement = dom} tabIndex="1" onKeyDown={this.handleKeydown}>
						<div ref={viewport => this.viewport = viewport} className="viewport" style={{ width: width + 16, height: height + 16 }}>
							<div className="paper">
								<img
									ref={image => this.image = image}
									onLoad={this.handleImageLoaded}
									src={imageUrl}
									crossOrigin="anonymous"
									style={{
										opacity: imageLoading ? 0 : 1,
										transition: 'opacity .3s ease, transform .3s ease',
										transform: imageLoading ? 'scale3d(0.8, 0.8, 1)' : 'scale3d(1, 1, 1)'
									}}
								/>
								<canvas
									ref={canvas => this.canvas = canvas}
									width={width}
									height={height}
									style={{cursor}}
									onMouseDown={this.handleDrawEvent}
									onMouseUp={this.handleDrawEvent}
									onMouseMove={this.handleDrawEvent}
								/>
								<div className="tool-elements">
									{ toolList.map(x => x.onRenderElement({
										width,
										height,
										brushWidth,
										brushColor,
										fontFamily,
										fontSize
									})).filter(Boolean) }
								</div>
							</div>
						</div>
						<br />
						<div className="tools-container" style={imageLoading ? { position: 'relative', top: '35%' } : null}>
							{ imageLoading ?
								<div className="load-msg">请稍候</div> :
								<Toolbar
									tools={toolList}
									activeMode={mode}
									commiting={commiting}
									onToggle={tool => this.toggleTool(tool.mode)}
									onUndo={this.undo}
									onCommit={this.commit}
									onCancel={this.cancel}>
								</Toolbar>
							}
							{ commitError && <div className="error-msg">保存失败：{commitError.message}</div> }
						</div>
						<br />
						<BrushToolbar
							mode={mode}
							active={!!mode}
							isTextBrush={mode == 'text'}
							brushWidth={brushWidth}
							brushColor={brushColor}
							fontSize={fontSize}
							anchorOffset={{
								rect: -185,
								ellipse: -142,
								arrow: -98,
								path: -54,
								text: -10,
								mosaic: 34,
							}[mode]}
							onChange={(brush) => this.setState(brush)}
						/>
					</div>
				}
			</CSSTransitionGroup>
		);
	}

	componentDidUpdate() {
		this.paint();
	}

	start({ imageUrl, imageSizeLimit, uploadImage, onFinish, onCancel, maxImageSize }) {
		// 必须强迫浏览器重新加载图片，缓存的图片没有跨域头会污染 canvas，导致无法导出
		// @see https://bugs.chromium.org/p/chromium/issues/detail?id=409090
		// @see https://stackoverflow.com/questions/12648809/cors-policy-on-cached-image
		if (imageUrl.indexOf('?') > 0) {
			imageUrl += '&';
		} else {
			imageUrl += '?';
		}
		imageUrl += '_=' + (+new Date());

		const start = isObjectUrl => {
			this.setState({ imageUrl, imageSizeLimit, uploadImage, isObjectUrl, onFinish, onCancel, maxImageSize });
			this.focus();
		};

		// IE10 和 Edge，Safari 不支持图片跨域协议，使用XHR加载
		if (/msie 10|edge|^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
			const xhr = new XMLHttpRequest();
			xhr.onload = () => {
				imageUrl = URL.createObjectURL(xhr.response);
				start(true);
			};
			xhr.open('GET', imageUrl, true);
			xhr.responseType = 'blob';
			xhr.send();
		} else {
			start();
		}
	}

	paint() {
		if (!this.canvas) return;
		const ctx = this.canvas.getContext('2d');
		ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

		let { drawings, activeDrawing } = this.state;
		if (activeDrawing) {
			drawings = drawings.concat({ ...activeDrawing, commited: false });
		}
		this.paintDrawings(ctx, drawings);
	}

	paintDrawings(ctx, drawings) {
		ctx.save();
		ctx.translate(0.5, 0.5);
		for (let drawing of drawings) {
			this.tools[drawing.type].onRenderCanvas(ctx, drawing);
		}
		ctx.restore();
	}

	createTools() {
		const createStore = (mode) => {
			const getState = () => this.state.toolState[mode] || {};
			const setState = (state) => this.setState({
				toolState: {
					...this.state.toolState,
					[mode]: {
						...getState(),
						...state,
					}
				}
			});
			return { getState, setState };
		};
		const tools = {};
		for (let mode of Object.keys(TOOLS)) {
			/** @type {Tool} */
			const Tool = TOOLS[mode];
			tools[mode] = new Tool({ store: createStore(mode) });
		}
		return tools;
	}

	toggleTool(mode) {
		this.deactiveTool();
		if (mode != this.state.mode) {
			this.activeTool(mode);
		}
	}

	activeTool(mode) {
		this.setState({ mode });
		this.tools[mode].onActived();
	}

	deactiveTool() {
		const { mode, drawings } = this.state;
		if (mode) {
			const { commit, drawing } = this.tools[mode].onDeactived();

			if (commit && drawing) {
				drawing.commited = true;
				this.setState({
					drawings: [...drawings, this.decorateDrawing(drawing)]
				});
			}
		}
		return new Promise(resolve => this.setState({ mode: null }, resolve));
	}

	focus() {
		setTimeout(() => {
			if (this.rootElement) {
				this.rootElement.focus();
			}
		}, 10);
	}

	handleImageLoaded = () => {
		if (this.image) {
			const { width, height } = this.image;
			this.setState({ width, height });
			setTimeout(() => {
				this.setState({ imageLoading: false });
				if (this.viewport) {
					this.viewport.scrollTop = (this.viewport.scrollHeight - this.viewport.clientHeight) / 2;
					this.viewport.scrollLeft = (this.viewport.scrollWidth - this.viewport.clientWidth) / 2;
				}
			}, 1);
			if (this.state.isObjectUrl) {
				URL.revokeObjectURL(this.state.imageUrl);
				this.setState({ isObjectUrl: false });
			}
		}
	}

	handleDrawEvent = (e) => {
		if (!this.canvas) return;

		const { mode, drawings, brushWidth, brushColor, fontFamily, fontSize } = this.state;
		if (!mode) return this.handleGrabEvent(e);

		const { left, top } = this.canvas.getBoundingClientRect();
		const type = e.type;
		const x = Math.round(e.clientX - left) || 0;
		const y = Math.round(e.clientY - top) || 0;
		const input = { type, x, y, e };

		const { commit, drawing } = this.tools[mode].onInput(input);

		if (!drawing) return;

		const decoratedDrawing = this.decorateDrawing(drawing);

		if (commit) {
			decoratedDrawing.commited = true;
			this.setState({
				drawings: [...drawings, decoratedDrawing],
				activeDrawing: null,
			});
		}
		else {
			this.setState({
				activeDrawing: decoratedDrawing,
			});
		}
	}

	handleGrabEvent = (e) => {
		const { clientX, clientY } = e;
		switch (e.type) {
			case 'mousedown':
				this.lastGrapPosition = { lastX: clientX, lastY: clientY };
				this.setState({ grabbing: true });
				break;
			case 'mousemove':
				if (this.lastGrapPosition) {
					const { lastX, lastY } = this.lastGrapPosition;
					const dx = clientX - lastX;
					const dy = clientY - lastY;
					if (this.viewport) {
						this.viewport.scrollLeft -= dx;
						this.viewport.scrollTop -= dy;
					}
					this.lastGrapPosition = { lastX: clientX, lastY: clientY };
				}
				break;
			case 'mouseup':
				this.lastGrapPosition = null;
				this.setState({ grabbing: false });
				break;
		}
	}

	decorateDrawing(drawing) {
		const { brushWidth, brushColor, fontFamily, fontSize } = this.state;
		return {
			...drawing,
			sourceImage: this.image,
			brushWidth,
			brushColor,
			fontFamily,
			fontSize,
			cache: {},
		};
	}

	handleKeydown = (e) => {
		const { mode } = this.state;
		this.handleDrawEvent(e);
		if (e.isDefaultPrevented()) {
			return;
		}
		// esc
		if (e.keyCode == 27) {
			if (mode) {
				this.deactiveTool();
			}
			else {
				this.cancel();
			}
			e.stopPropagation();
		}
		// ctrl + z 撤销
		if (e.ctrlKey && e.keyCode == 90) {
			this.undo();
		}
	}

	undo = () => this.setState({ drawings: this.state.drawings.slice(0, -1) })

	cancel = () => {
		if (typeof this.state.onCancel == 'function') {
			this.state.onCancel();
		}
		this.close();
	}

	close = () => {
		this.deactiveTool();
		this.setState(this.getInitialState());
	}

	commit = async () => {
		await this.deactiveTool();

		const { width, height } = this.canvas;
		const { drawings, imageSizeLimit, uploadImage } = this.state;
		const finish = url => {
			if (typeof this.state.onFinish == 'function') {
				this.state.onFinish({ url });
			}
			this.close();
		};

		if (!drawings.length) {
			this.close();
			return;
		}

		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext('2d');
		ctx.drawImage(this.image, 0, 0);
		this.paintDrawings(ctx, drawings);

		try {
			this.setState({ commiting: true, commitError: null });
			const { blob, type } = await exportCanvasImage(canvas, imageSizeLimit);

			const extName = type === 'image/png' ? '.png' : '.jpg';
			let file = blob;
			file.name = 'edited' + extName;

			const { url } = await uploadImage(file);
			finish(url);
		} catch (err) {
			console.error(err);
			this.setState({ commitError: err, commiting: false });
		}
	}
}



// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/plugins/imagePlugin/ImageEditor/ImageEditor.jsx